import React, { useEffect, useState } from 'react';
import { getEmailHistoryByEmailId } from '../../services/EmailService';

function EmailHistorySidebar({ isOpen, onClose, emailId, subject }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(null);

  useEffect(() => {
    if (isOpen && emailId) {
      fetchHistory();
    }
  }, [isOpen, emailId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await getEmailHistoryByEmailId(emailId);
      setHistory(data || []);
      // Set email details if available in the first history entry
      if (data && data.length > 0) {
        setEmail(data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch email history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-success';
      case 'open':
        return 'bg-info';
      case 'click':
        return 'bg-primary';
      case 'sent':
      case 'processed':
      case 'created':
        return 'bg-secondary';
      case 'bounce':
      case 'deferred':
      case 'spamreport':
        return 'bg-danger';
      case 'unsubscribe':
        return 'bg-warning text-dark';
      default:
        return 'bg-dark text-white';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>

      {/* Sidebar */}
      <div
        className="offcanvas offcanvas-end show"
        style={{ width: '500px', zIndex: 1045 }}
        tabIndex="-1"
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">
            <i className="bi bi-clock-history me-2"></i>
            Email History
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          {email && (
            <div className="mb-4">
              <div className="card">
                <div className="card-body">
                  <h6 className="card-title">Email Details</h6>
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Subject</small>
                      <p className="mb-2">{subject || email.subject || 'N/A'}</p>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Current Status</small>
                      <p className="mb-2">
                        <span className={`badge ${getStatusBadgeClass(email.status || email.eventType)}`}>
                          {email.status || email.eventType || 'Unknown'}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Recipient</small>
                      <p className="mb-2">{email.recipientEmail || email.to || 'N/A'}</p>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Sender</small>
                      <p className="mb-2">{email.senderEmail || email.from || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <small className="text-muted">Sent Date</small>
                      <p className="mb-0">
                        {email.unixTimestamp ? 
                          new Date(email.unixTimestamp * 1000).toLocaleDateString() + ' ' + 
                          new Date(email.unixTimestamp * 1000).toLocaleTimeString() :
                          email.createdAt ? 
                          new Date(email.createdAt).toLocaleDateString() + ' ' + 
                          new Date(email.createdAt).toLocaleTimeString() : 'N/A'
                        }
                      </p>
                    </div>
                    <div className="col-6">
                      <small className="text-muted">Email ID</small>
                      <p className="mb-0 text-break">{emailId || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <h6 className="mb-3">Status History</h6>
          
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
              <p className="mt-2">No history available</p>
            </div>
          ) : (
            <div style={{
              position: 'relative',
              paddingLeft: '30px'
            }}>
              <div style={{
                content: '',
                position: 'absolute',
                left: '15px',
                top: 0,
                bottom: 0,
                width: '2px',
                background: '#e9ecef'
              }}></div>
              
              {history.map((entry, index) => (
                <div key={index} style={{
                  position: 'relative',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '-22px',
                    top: 0
                  }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: '2px solid #fff',
                      boxShadow: '0 0 0 2px #e9ecef',
                      backgroundColor: getStatusBadgeClass(entry.status || entry.eventType).includes('success') ? '#28a745' :
                                    getStatusBadgeClass(entry.status || entry.eventType).includes('info') ? '#17a2b8' :
                                    getStatusBadgeClass(entry.status || entry.eventType).includes('primary') ? '#007bff' :
                                    getStatusBadgeClass(entry.status || entry.eventType).includes('secondary') ? '#6c757d' :
                                    getStatusBadgeClass(entry.status || entry.eventType).includes('danger') ? '#dc3545' :
                                    getStatusBadgeClass(entry.status || entry.eventType).includes('warning') ? '#ffc107' :
                                    getStatusBadgeClass(entry.status || entry.eventType).includes('dark') ? '#343a40' : '#6c757d'
                    }}></div>
                  </div>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '1rem',
                    borderRadius: '0.375rem',
                    borderLeft: '3px solid #dee2e6'
                  }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">{entry.status || entry.eventType || 'Unknown Status'}</h6>
                        {entry.statusDetails && (
                          <p className="text-muted mb-1 small">{entry.statusDetails}</p>
                        )}
                        {entry.reason && (
                          <p className="text-danger mb-1 small">Reason: {entry.reason}</p>
                        )}
                        {entry.url && (
                          <a href={entry.url} target="_blank" rel="noopener noreferrer" className="small text-primary d-block">
                            {entry.url}
                          </a>
                        )}
                      </div>
                      <small className="text-muted">
                        {entry.unixTimestamp ? 
                          new Date(entry.unixTimestamp * 1000).toLocaleDateString() + ' ' + 
                          new Date(entry.unixTimestamp * 1000).toLocaleTimeString() :
                          entry.createdAt ? 
                          new Date(entry.createdAt).toLocaleDateString() + ' ' + 
                          new Date(entry.createdAt).toLocaleTimeString() : 'N/A'
                        }
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default EmailHistorySidebar; 